import React, { useEffect, useState } from 'react';
import tw from 'twin.macro';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { obtenerPedidosUsuario } from 'scripts/obtenerPedidosUsuario';
import { toast } from 'react-toastify';
import { estadosPedidosDescripcion } from 'utils/enums';

const Container = tw.div`p-6 bg-white rounded-lg w-full max-w-screen-xl mx-auto`;
const Title = tw.h2`text-2xl font-bold mb-6 text-center`;
const EmptyState = tw.div`flex flex-col items-center text-center py-8`;
const EmptyMessage = tw.p`text-gray-600 text-base mb-4`;
const StartShoppingButton = tw.button`bg-[#68a38a] text-white font-semibold px-6 py-3 rounded-lg transition duration-200 hover:bg-[#5a8e78]`;
const PedidoCard = tw.div`bg-gray-100 p-4 rounded-lg shadow mb-4 cursor-pointer`;
const PedidoHeader = tw.div`flex justify-between items-center`;
const PedidoId = tw.p`text-lg font-bold text-gray-700`;
const Estado = styled.span`
	${tw`px-3 py-1 rounded-full text-sm font-semibold`}
	${({ estado }) => {
		switch (estado) {
			case 1:
				return tw`bg-blue-200 text-blue-700`;
			case 2:
				return tw`bg-yellow-200 text-yellow-700`;
			case 3:
				return tw`bg-green-200 text-green-700`;
			case 4:
				return tw`bg-gray-200 text-gray-700`;
			default:
				return tw`bg-red-200 text-red-700`;
		}
	}}
`;
const EstadoPago = styled.span`
	${tw`px-3 py-1 rounded-full text-sm font-semibold`}
	${({ estado }) => {
		switch (estado) {
			case 'APROBADO':
				return tw`bg-green-200 text-green-700`;
			case 'PENDIENTE':
				return tw`bg-yellow-200 text-yellow-700`;
			case 'RECHAZADO':
				return tw`bg-red-200 text-red-700`;
			default:
				return tw`bg-gray-200 text-gray-700`;
		}
	}}
`;
const Total = tw.p`text-lg font-bold text-gray-700`;
const MontoPagado = tw.p`text-sm font-semibold text-gray-600`;
const Diferencia = tw.p`text-sm font-semibold text-red-600`;
const Fecha = tw.p`text-sm text-gray-500`;
const Notas = tw.p`text-[15px] font-bold text-gray-700`;
const ProductosList = tw.div`mt-4 border-t pt-4`;
const ProductoItem = tw.div`flex justify-between items-center py-2`; // Flex para alineación horizontal
const ProductoNombre = tw.div`text-gray-700 font-medium flex-1`; // Ocupa el espacio restante
const ProductoCantidad = tw.div`text-gray-500 w-24 text-right`; // Alineación a la derecha y ancho fijo
const ProductoPrecio = tw.div`text-gray-700 font-semibold w-24 text-right`; // Alineación a la derecha y ancho fijo

const PedidosPage = () => {
	const [pedidos, setPedidos] = useState([]);
	const [pedidoSeleccionado, setPedidoSeleccionado] = useState(null);
	const [productos, setProductos] = useState([]);
	const user = useSelector((state) => state.usuario.userInfo);
	const navigate = useNavigate();
	const token = sessionStorage.getItem('token');

	const calcularTotalPedido = (pedido) => {
		if (!pedido?.detalles || pedido.detalles.length === 0) {
			return Number(pedido?.total || 0);
		}
		return pedido.detalles.reduce((acc, detalle) => acc + Number(detalle?.subtotal || 0), 0);
	};

	const obtenerMontoPagado = (pedido) => {
		if (pedido?.monto_pagado === null || pedido?.monto_pagado === undefined) {
			return null;
		}
		const monto = Number(pedido.monto_pagado);
		return Number.isNaN(monto) ? null : monto;
	};

	const togglePedido = (id) => {
		setPedidoSeleccionado((prev) => (prev === id ? null : id));
	};

	// Cargar productos y packs desde Redux
	useEffect(() => {
		const fetchPedidos = () => {
			obtenerPedidosUsuario(user.id, token)
				.then((data) => {
					setPedidos(data);
				})
				.catch((error) => {
					toast.error(error.message);
				});
		};
		fetchPedidos();
	}, []);

	useEffect(() => {
		if (pedidoSeleccionado) {
			const pedido = pedidos.filter((pedido) => pedido.id === pedidoSeleccionado);
			setProductos(pedido[0].detalles);
		}
	}, [pedidoSeleccionado]);

	return (
		<Container>
			<Title>Mis Pedidos</Title>
			{pedidos.length === 0 && (
				<EmptyState>
					<EmptyMessage>Aún no hay pedidos.</EmptyMessage>
					<StartShoppingButton onClick={() => navigate('/productos')}>
						Ir a Productos para empezar a comprar
					</StartShoppingButton>
				</EmptyState>
			)}
			{pedidos.map((pedido) => (
				<PedidoCard key={pedido.id} onClick={() => togglePedido(pedido.id)}>
					<PedidoHeader>
						<div>
							<PedidoId>Pedido #{pedido.id}</PedidoId>
							<Estado estado={pedido.estado}>{estadosPedidosDescripcion[pedido.estado]}</Estado>
							<EstadoPago estado={pedido.estado_pago}>Estado de pago: {pedido.estado_pago}</EstadoPago>
							<Notas>Notas: {pedido.notas}</Notas>
							<Fecha>Fecha: {pedido.fechaCreacion}</Fecha>
						</div>
						<div>
							<Total>Total pedido: ${calcularTotalPedido(pedido).toFixed(2)}</Total>
							<MontoPagado>
								Monto pagado:{' '}
								{obtenerMontoPagado(pedido) !== null ?
									`$${obtenerMontoPagado(pedido).toFixed(2)}`
								:	'Pendiente'}
							</MontoPagado>
							{obtenerMontoPagado(pedido) !== null &&
								Math.abs(calcularTotalPedido(pedido) - obtenerMontoPagado(pedido)) > 0.01 && (
									<Diferencia>
										Diferencia: $
										{Math.abs(calcularTotalPedido(pedido) - obtenerMontoPagado(pedido)).toFixed(2)}
									</Diferencia>
								)}
						</div>
					</PedidoHeader>
					{pedidoSeleccionado === pedido.id && (
						<ProductosList>
							{productos.map((producto, index) => (
								<ProductoItem key={index}>
									<ProductoNombre>{producto.descripcion}</ProductoNombre>
									<ProductoCantidad>Cantidad: {producto.cantidad}</ProductoCantidad>
									<ProductoPrecio>${producto.subtotal}</ProductoPrecio>
								</ProductoItem>
							))}
						</ProductosList>
					)}
				</PedidoCard>
			))}
		</Container>
	);
};

export default PedidosPage;
